import { Center, Container, Loader, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { Activity } from '@/api/generated/models';
import type { StatusHistoryEntry } from '@/features/history/statusHistoryTypes';
import { useGetActivity, useGetActivityHistory } from '@/api/generated/activities/activities';
import { BackButton, ContentCard } from '@/components/common';
import { activityLockConfig } from '@/features/activities/activityDisplay';
import { StatusHistoryTable } from '@/features/history/components/StatusHistoryTable';
import { formatWeekLabel } from '@/features/week-reports/weekReportDisplay';

export const ActivityHistoryPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: activityData, isLoading: isLoadingActivity } = useGetActivity(id ?? '', {
    query: { enabled: !!id }
  });
  const activity = activityData as Activity | undefined;
  const { data: historyData, isLoading: isLoadingHistory } = useGetActivityHistory(id ?? '', {
    query: { enabled: !!id }
  });
  const entries = (historyData as StatusHistoryEntry[] | undefined) ?? [];

  // Aktivnost nema pravi status stupac (AUD-01) — backend mapira weekReportId null/ne-null na
  // RECORDED/IN_REPORT, isti model kao activityLockState (badge na samoj Aktivnosti).
  const resolveStatus = (raw: string) => {
    const config = raw === 'IN_REPORT' ? activityLockConfig.locked : activityLockConfig.recorded;
    return { label: t(config.labelKey), bg: config.bg, fg: config.fg };
  };

  if (isLoadingActivity || isLoadingHistory || !activity) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to={`/activities/${id}`} />
        <Text fz={14} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
          {formatWeekLabel(activity.weekNumber, activity.reportYear)}
        </Text>
        <ContentCard>
          <div style={{ padding: 20 }}>
            <StatusHistoryTable entries={entries} resolveStatus={resolveStatus} />
          </div>
        </ContentCard>
      </Stack>
    </Container>
  );
};
